/**
 * @module UsePlatformEvents
 * @purpose Fetch the recent platform-level audit event log (demo-mode toggles, provider
 *   switches) for the platform admin settings page.
 * @depends services/platform (listPlatformEvents)
 * @dependedOnBy pages/platform-settings/PlatformEventLog
 *
 * @rules
 * - Performance: single fetch on mount; no polling -- the page offers a manual refetch
 * - State: local useState; no shared context
 * - Errors: sets an error string on failure; never throws out of the hook
 * - Note: the backend returns events newest-first already -- this hook renders the
 *   response order verbatim (house rule: the frontend never sorts or filters data)
 */

import { useCallback, useEffect, useState } from 'react'
import { listPlatformEvents } from '../services/platform'
import { logger } from '../utils/logger'
import type { PlatformEvent } from '../types/platform'

export interface UsePlatformEventsResult {
  events: PlatformEvent[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the platform event log.
 *
 * @behaviour Fetches once on mount; `refetch` re-runs the same request on demand. `loading`
 *            starts `true` so the mount fetch never renders a one-frame empty-state flash.
 * @errors Sets `error` to a message string on failure; `events` resets to `[]`.
 */
export function usePlatformEvents(): UsePlatformEventsResult {
  const [events, setEvents] = useState<PlatformEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const result = await listPlatformEvents()
      setEvents(result.events)
    } catch (err) {
      logger.warn('usePlatformEvents', 'Failed to fetch platform events', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch platform events')
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { events, loading, error, refetch: fetch }
}
